import { useQueries } from "@tanstack/react-query";
import { ApiError, type AttachmentStatusItem, attachmentStatus } from "@/api";

type Lifecycle = AttachmentStatusItem["lifecycle"];

/** Being checked (MHS-646): sending waits for these. */
const CHECKING = new Set<Lifecycle>(["uploading", "pending_start", "queued", "preparing"]);
/** Checks that can't run right now (the preparation runtime or the virus scanner is down): the file waits. */
const CHECKS_UNAVAILABLE = new Set(["STARTER_UNAVAILABLE", "VALIDATION_UNAVAILABLE"]);
/** Can't be used: the composer removes them and says why. */
export const UNUSABLE = new Set<Lifecycle>(["failed", "rejected", "cancelled"]);
/** The status endpoint's bound on ids per request. */
const IDS_PER_REQUEST = 200;
const POLL_MS = 1_500;
/** Query key prefix of the status reads (live file notices refetch them: useChatFileEvents). */
export const FILE_CHECKS_KEY = "chatFileChecks";

/** A status read that fails is retried this many times, backing off, before polling stops (PR #336 review). */
export const MAX_RETRIES = 3;
const retryDelay = (attempt: number) => Math.min(POLL_MS * 2 ** (attempt + 1), 15_000);
// As the app's default: a client error won't fix itself
const retry = (failures: number, error: unknown) =>
  !(error instanceof ApiError && error.status >= 400 && error.status < 500) && failures < MAX_RETRIES;

/**
 * "unavailable": still waiting for checks that can't run right now. "unknown": the status couldn't be read (the
 * status request kept failing) or the backend doesn't know the file. Sending waits for neither: the assistant leaves
 * an unchecked file out of the reply, and the composer says so.
 */
export type FileCheck = "checking" | "unavailable" | "unknown" | "usable" | "unusable";

/** Where a file's check stands. Ready, or not checked yet because no capacity was free (checked on first use). */
export function checkOf(status: AttachmentStatusItem | undefined): FileCheck {
  if (!status) return "checking";
  if (CHECKING.has(status.lifecycle))
    return CHECKS_UNAVAILABLE.has(status.reasonCode ?? "") ? "unavailable" : "checking";
  return UNUSABLE.has(status.lifecycle) ? "unusable" : "usable";
}

const waiting = (check: FileCheck) => check === "checking" || check === "unavailable";

type Checked = { items: AttachmentStatusItem[]; missing: string[] };

/**
 * Status of uploaded files while the file pipeline checks them (virus scan, type check), and which of them are
 * "unknown". Polls only while some are still being checked; ids are read in bounded requests. A request that keeps
 * failing stops polling (one report, not one per poll) until a live file notice reads it again
 * (useChatFileEvents). Disabled (empty) when the deployment has no pipeline.
 */
export function useFileChecks(
  fileIds: readonly string[],
  enabled: boolean,
): { statuses: Map<string, AttachmentStatusItem>; unknown: Set<string> } {
  const chunks: string[][] = [];
  if (enabled)
    for (let i = 0; i < fileIds.length; i += IDS_PER_REQUEST) chunks.push(fileIds.slice(i, i + IDS_PER_REQUEST));
  const results = useQueries({
    queries: chunks.map((ids) => ({
      queryKey: [FILE_CHECKS_KEY, ids] as const,
      queryFn: async ({ signal }: { signal: AbortSignal }): Promise<Checked> => {
        const { data } = (await attachmentStatus({ body: { fileIds: ids }, signal, throwOnError: true })).data;
        return { items: data.items, missing: data.missing ?? [] };
      },
      retry,
      retryDelay,
      refetchInterval: (query: { state: { status: string; data?: Checked } }) => {
        if (query.state.status === "error") return false;
        return query.state.data?.items.some((s) => waiting(checkOf(s))) === false ? false : POLL_MS;
      },
    })),
  });
  const statuses = new Map<string, AttachmentStatusItem>();
  const unknown = new Set<string>();
  results.forEach((result, i) => {
    for (const status of result.data?.items ?? []) statuses.set(status.fileId, status);
    for (const id of result.data?.missing ?? []) unknown.add(id);
    // Polling stopped on errors: whatever was still waiting is now unknown, not stuck on "Checking…"
    if (result.isError) for (const id of chunks[i] ?? []) if (waiting(checkOf(statuses.get(id)))) unknown.add(id);
  });
  return { statuses, unknown };
}
